import React, { FC, memo } from 'react';
import { Drawer } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { WarehouseForBooking, getWorkingScheduleString } from 'entities/Warehouse';
import { Button } from 'shared/ui/Button';
import { UnifiedWarehouseMap } from 'widgets/UnifiedWarehouseMap';
import { ModernSizeGroupsTags } from 'entities/SizeCode';
import { getBoxDiscountedPriceAmount, getBoxPriceAmountWithVat, useGetMinBoxPriceAmount } from 'entities/Box';
import { useGetCurrencySymbol, getGlobalSettings } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getLoggedUserData } from 'entities/User';
import { ReactComponent as CloseIcon } from 'shared/assets/icons/CloseIcon.svg';
import s from './SelectWarehouseMapMobileYandex.module.scss';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface ModernWarehouseDrawerMobileProps {
  isOpen: boolean;
  onClose: () => void;
  warehouse: WarehouseForBooking;
  onSelectBox: (boxId: string, sizeCodeId: string) => void;
  onBookBox: (boxId: string, sizeCodeId: string) => void;
}

export const ModernWarehouseDrawerMobile: FC<ModernWarehouseDrawerMobileProps> = memo((props) => {
  const { isOpen, onClose, warehouse, onBookBox } = props;
  const { t } = useAppTranslation(['booking', 'common']);

  const currencySymbol = useGetCurrencySymbol();
  const globalSettings = useAppSelector(getGlobalSettings);
  const loggedUser = useAppSelector(getLoggedUserData);

  const workingScheduleString = getWorkingScheduleString(warehouse.workingSchedule, t);
  const minBoxPrice = useGetMinBoxPriceAmount(warehouse);
  const discountedPrice = getBoxDiscountedPriceAmount({ warehouseDiscounts: warehouse.warehouseDiscounts, boxPrice: warehouse.minPrice });
  const discountedBoxPriceWithVat = getBoxPriceAmountWithVat(discountedPrice, warehouse, loggedUser?.accountType);
  const discountedBoxPrice = globalSettings?.showPriceWithVat ? discountedBoxPriceWithVat : discountedPrice;

  return (
    <Drawer placement="bottom" height="520px" open={isOpen} onClose={onClose} className={s.drawer} closable={false}>
      <div className="-m-6">
        <div className="flex items-center justify-between p-4">
          <h3 className="text-2xl font-semibold text-primary font-tektur">{warehouse.name}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
            <CloseIcon className="[&>path]:stroke-primary" width={25} height={25} />
          </button>
        </div>
        <div className="h-[200px]">
          <UnifiedWarehouseMap
            warehouseInfo={warehouse}
            mapContainerClassName="w-full h-full"
            zoom={15}
            mapId="warehouseDrawerMap"
            withMarkerPopups={false}
          />
        </div>

        <div className="h-[240px]">
          <div className="p-4">
            <div className="space-y-2 text-sm text-grey mb-3">
              <div>{warehouse.address}</div>
              <div>{workingScheduleString}</div>
            </div>

            {warehouse.sizeCodes && (
              <div className="mb-3">
                <ModernSizeGroupsTags sizeCodes={warehouse.sizeCodes} />
              </div>
            )}

            <div className="text-grey text-md mb-3">
              {t('{{availableBoxesAmount}} boxes free', { availableBoxesAmount: warehouse.availableBoxesAmount })}
            </div>

            {Boolean(minBoxPrice) && (
              <div className="text-lg ml-auto font-semibold text-primary mb-3 font-tektur">
                {t('From {{discountedPrice}} {{currencySymbol}}/month', {
                  currencySymbol,
                  discountedPrice: formatPrice(discountedBoxPrice || minBoxPrice),
                })}
              </div>
            )}

            <Button
              className="w-full"
              theme="primary"
              onClick={() => {
                onBookBox('', '');
              }}
            >
              {t('View boxes')}
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
});
